//core
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Chip,
  Autocomplete,
  TextField,
  Typography,
  Tooltip,
  IconButton,
  ClickAwayListener,
  useTheme,
} from "@mui/material";

//icons
import AddIcon from "@mui/icons-material/AddBox";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";

//styles
import { useStyles } from "./styles";

//validation
import { validationTagName } from "../../../validation/tagName.validation";

//constants
import { getNextTagColor, getTagDisplayColor } from "./constants";

export interface ITag {
  id: string | number;
  name: string;
  color?: string;
}

export type TagSelectVariant = "default" | "collapsible" | "compact";

interface ITagSelectProps {
  value: ITag[];
  onChange: (tags: ITag[]) => void;
  onCreateTag?: (name: string, color: string) => Promise<ITag | null> | ITag | null;
  onAssignTag?: (tagIds: number[]) => Promise<void> | void;
  onRemoveTag?: (tagIds: number[]) => Promise<void> | void;
  getOptions?: () => Promise<ITag[]> | ITag[];
  options?: ITag[];
  disabled?: boolean;
  variant?: TagSelectVariant;
  maxVisibleChips?: number;
  showAddOnHover?: boolean;
}

const TAG_MAX_LENGTH = 20;

const truncateTagName = (name: string): string => {
  if (name.length > TAG_MAX_LENGTH) {
    return `${name.slice(0, TAG_MAX_LENGTH)}...`;
  }
  return name;
};

const TagSelect: React.FC<ITagSelectProps> = ({
  value,
  onChange,
  onCreateTag,
  onAssignTag,
  onRemoveTag,
  getOptions,
  options = [],
  disabled = false,
  variant = "default",
  maxVisibleChips = 4,
  showAddOnHover = false,
}): JSX.Element => {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [inputValue, setInputValue] = useState("");
  const [loadedOptions, setLoadedOptions] = useState<ITag[]>(Array.isArray(options) ? options : []);
  const [open, setOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const isCollapsible = variant === "collapsible";
  const hasMore = isCollapsible && value.length > maxVisibleChips;
  const isCollapsed = isCollapsible && !expanded;
  const visibleTags = isCollapsed && hasMore ? value.slice(0, maxVisibleChips) : value;

  const [errorsMessages, setErrorsMessages] = useState({
    required: t("common.validation.required_field"),
    maxLength: t("common.validation.max_length"),
    noSpaces: t("common.validation.noSpaces"),
    duplicate: t("common.validation.duplicate"),
    invalidCharacter: t("common.validation.invalid_character"),
  });

  useEffect(() => {
    setErrorsMessages({
      required: t("common.validation.required_field"),
      maxLength: t("common.validation.max_length"),
      noSpaces: t("common.validation.noSpaces"),
      duplicate: t("common.validation.duplicate"),
      invalidCharacter: t("common.validation.invalid_character"),
    });
  }, [i18n.language, t]);

  const handleOpen = useCallback(async () => {
    if (disabled) return;

    setOpen(true);

    if (getOptions) {
      setIsLoading(true);
      try {
        const options = await getOptions();
        setLoadedOptions(Array.isArray(options) ? options : []);
      } finally {
        setIsLoading(false);
      }
    }
  }, [disabled, getOptions]);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setInputValue("");
  }, []);

  const handleClearAll = useCallback(() => {
    onChange([]);
    if (onRemoveTag && value.length > 0) {
      const tagIds = value.map((tag) => Number(tag.id));
      onRemoveTag(tagIds);
    }
  }, [onChange, onRemoveTag, value]);

  const handleRemoveTag = useCallback(
    (tagToRemove: ITag) => {
      onChange(value.filter((tag) => tag.id !== tagToRemove.id));
      if (onRemoveTag) {
        onRemoveTag([Number(tagToRemove.id)]);
      }
    },
    [value, onChange, onRemoveTag]
  );

  const handleAddButtonClick = useCallback(() => {
    handleOpen();
  }, [handleOpen]);

  const handleSelectOption = useCallback(
    async (_event: any, newValue: ITag | null) => {
      if (!newValue) return;

      if (
        typeof newValue.id === "string" &&
        (newValue.id as string).startsWith("__new__")
      ) {
        if (!onCreateTag) return;

        setIsCreating(true);
        try {
          const color = getNextTagColor(loadedOptions);
          const createdTag = await onCreateTag(newValue.name, color);
          if (createdTag) {
            setLoadedOptions((prev) => [...prev, createdTag]);
            onChange([...value, createdTag]);
            if (onAssignTag) {
              onAssignTag([Number(createdTag.id)]);
            }
          }
        } finally {
          setIsCreating(false);
        }
      } else {
        onChange([...value, newValue]);
        if (onAssignTag) {
          onAssignTag([Number(newValue.id)]);
        }
      }

      setInputValue("");
    },
    [value, onChange, onCreateTag, onAssignTag, loadedOptions]
  );

  const inputError = useMemo(() => {
    if (!inputValue) return "";

    const error = validationTagName(inputValue, errorsMessages);
    if (error) return error;

    const alreadySelected = value.some(
      (tag) => tag.name.toLowerCase() === inputValue.toLowerCase()
    );

    const hasAvailableOptions = loadedOptions.some(
      (opt) =>
        opt.name.toLowerCase().includes(inputValue.toLowerCase()) &&
        !value.some((tag) => tag.id === opt.id)
    );

    if (alreadySelected && !hasAvailableOptions) return errorsMessages.duplicate;

    return "";
  }, [inputValue, value, loadedOptions, errorsMessages]);

  const filteredOptions = useMemo(() => {
    const selectedIds = new Set(value.map((t) => t.id));
    const available = loadedOptions.filter((opt) => !selectedIds.has(opt.id));

    if (!inputValue.trim()) return available;

    const searchLower = inputValue.toLowerCase();
    const matched = available.filter((opt) =>
      opt.name.toLowerCase().includes(searchLower)
    );

    const exactExists = loadedOptions.some(
      (opt) => opt.name.toLowerCase() === searchLower
    );

    if (!exactExists && onCreateTag && !inputError) {
      const newTagOption: ITag = {
        id: `__new__${inputValue}`,
        name: inputValue,
        color: getNextTagColor(loadedOptions),
      };
      return [newTagOption, ...matched];
    }

    return matched;
  }, [loadedOptions, value, inputValue, onCreateTag, inputError]);

  if (!open) {
    if (variant === "compact") {
      const visibleTags = value.slice(0, maxVisibleChips);
      const hiddenCount = value.length - visibleTags.length;

      return (
        <Box
          className={`${styles.compactContainer} ${disabled ? styles.compactDisabled : ''}`}
          onClick={!disabled && value.length > 0 ? handleAddButtonClick : undefined}
        >
          {value.length === 0 ? (
            <Tooltip title={t("common.components.tagSelect.tooltip")} placement="top">
              <IconButton
                className={`${styles.iconBtn} ${showAddOnHover ? styles.addOnHover : ''}`}
                size="small"
                disabled={disabled}
                onClick={!disabled ? handleAddButtonClick : undefined}
                aria-label="add tag"
              >
                <AddIcon fontSize="small" className={styles.icon} />
              </IconButton>
            </Tooltip>
          ) : (
            <Box className={styles.compactChipsRow}>
              {visibleTags.map((tag) => {
                const isTruncated = tag.name.length > TAG_MAX_LENGTH;
                const chip = (
                  <Chip
                    key={tag.id}
                    label={truncateTagName(tag.name)}
                    size="small"
                    variant="outlined"
                    sx={tag.color ? { backgroundColor: getTagDisplayColor(tag.color, isDark), borderColor: getTagDisplayColor(tag.color, isDark) } : undefined}
                  />
                );
                return isTruncated ? (
                  <Tooltip key={tag.id} title={tag.name} placement="top">
                    {chip}
                  </Tooltip>
                ) : chip;
              })}
              {hiddenCount > 0 && (
                <Typography
                  variant="body2"
                  className={styles.moreCount}
                >
                  +{hiddenCount}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      );
    }

    return (
      <Box className={styles.closedContainer}>
        <Box className={styles.chipsRow}>
          {visibleTags.map((tag) => {
            const isTruncated = tag.name.length > TAG_MAX_LENGTH;
            const chip = (
              <Chip
                key={tag.id}
                label={truncateTagName(tag.name)}
                size="small"
                variant="outlined"
                disabled={disabled}
                sx={tag.color ? { backgroundColor: getTagDisplayColor(tag.color, isDark), borderColor: getTagDisplayColor(tag.color, isDark) } : undefined}
              />
            );
            return isTruncated ? (
              <Tooltip key={tag.id} title={tag.name} placement="top">
                {chip}
              </Tooltip>
            ) : chip;
          })}
          {!disabled && (
            <Tooltip title={t("common.components.tagSelect.tooltip")} placement="top">
              <IconButton
                className={styles.iconBtn}
                size="small"
                onClick={handleAddButtonClick}
                aria-label="add tag"
              >
                <AddIcon fontSize="small" className={styles.icon} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {hasMore && (
          <IconButton
            size="small"
            onClick={toggleExpanded}
            className={styles.iconBtn}
            aria-label={expanded ? "collapse tags" : "expand tags"}
          >
            <ExpandMoreIcon
              fontSize="small"
              className={styles.icon}
              sx={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </IconButton>
        )}
      </Box>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box className={styles.root}>
        <Autocomplete
        open
        size="small"
        loading={isLoading}
        disabled={isCreating}
        disableCloseOnSelect
        autoHighlight
        options={filteredOptions}
        getOptionLabel={(option) => option.name}
        inputValue={inputValue}
        onInputChange={(_e, newInputValue, reason) => {
          if (reason === "input") {
            setInputValue(newInputValue);
          }
        }}
        onChange={handleSelectOption}
        onClose={(_e, reason) => {
          if (reason === "escape") {
            handleClose();
          }
        }}
        clearOnBlur={false}
        disableClearable
        isOptionEqualToValue={(option, val) => option.id === val.id}
        noOptionsText={
          inputError
            ? <Typography variant="body2" color="error">{inputError}</Typography>
            : t("common.components.autocomplete.no_matches")
        }
        className={styles.autocomplete}
        ListboxProps={{
          className: styles.listbox,
        }}
        renderOption={(props, option) => {
          const isNewTag =
            typeof option.id === "string" &&
            (option.id as string).startsWith("__new__");

          return (
            <Box
              sx={{ minHeight: 'auto!important' }}
              component="li"
              {...props}
              key={option.id}
            >
              <Chip
                label={option.name}
                size="small"
                variant="outlined"
                sx={{
                  cursor: "pointer",
                  height: "auto",
                  "& .MuiChip-label": {
                    whiteSpace: "normal",
                    wordBreak: "break-all",
                  },
                  ...(option.color ? { backgroundColor: getTagDisplayColor(option.color, isDark), borderColor: getTagDisplayColor(option.color, isDark) } : {}),
                }}
              />
              {isNewTag && (
                <Typography variant="body2" className={styles.newTagHint}>
                  {t('common.components.tagSelect.newTag')}
                </Typography>
              )}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            placeholder={t("common.components.autocomplete.placeholder")}
            error={!!inputError}
            variant="outlined"
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: value.length > 0 ? (
                <Box
                  sx={{
                    paddingRight: '30px',
                    boxSizing: 'initial',
                  }}
                >
                  {value.map((tag) => {
                    const isTruncated = tag.name.length > TAG_MAX_LENGTH;
                    const chip = (
                      <Chip
                        key={tag.id}
                        label={truncateTagName(tag.name)}
                        size="small"
                        variant="outlined"
                        onDelete={() => handleRemoveTag(tag)}
                        className={styles.chipInInput}
                        sx={tag.color ? { backgroundColor: getTagDisplayColor(tag.color, isDark), borderColor: getTagDisplayColor(tag.color, isDark) } : undefined}
                      />
                    );
                    return isTruncated ? (
                      <Tooltip key={tag.id} title={tag.name} placement="top">
                        {chip}
                      </Tooltip>
                    ) : chip;
                  })}
                </Box>
              ) : undefined,
              endAdornment: (
                <Box className={styles.endAdornment}>
                  {value.length > 0 && (
                    <IconButton
                      size="small"
                      onClick={handleClearAll}
                      aria-label="clear all tags"
                      className={styles.clearButton}
                    >
                      <ClearIcon fontSize="small" className={styles.icon} />
                    </IconButton>
                  )}
                  <ArrowDropDownIcon
                    fontSize="medium"
                    className={styles.icon}
                    onClick={handleClose}
                    sx={{ cursor: 'pointer' }}
                  />
                </Box>
              ),
            }}
          />
        )}
      />
      </Box>
    </ClickAwayListener>
  );
};

export default TagSelect;
